import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Divider } from "@mui/material";
import { toast } from "react-toastify";
import EditOutlined from "@mui/icons-material/EditOutlined";

import {
  TaskDetailContext,
  useTaskDetailContext,
} from "contexts/TaskDetailContext";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TaskBasicDetail from "./taskComponents/TaskBasicDetail";
import TaskResponses from "./taskComponents/TaskResponses";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import StagePathway from "components/Shared/StagePathway";
import EnableDisableButton from "components/Shared/EnableDisableButton";

import {
  useGetTaskDetailQuery,
  useUpdateTaskMutation,
} from "store/services/taskApi";
import { useLazyListListQuery } from "store/services/listService";

import { useAppMeta } from "hooks";

import { ENTITY_LIST_CONSTANTS } from "data/static";

const TaskDetailScreen = () => {
  const { id } = useParams();

  const breadcrumbs = [
    { title: "Tasks", path: "/app/crm/task/list" },
    {
      title: "Task Detail",
    },
  ];

  // RTK Query
  const { data: taskDetail, isLoading, error } = useGetTaskDetailQuery(id);

  const [
    fetchStages,
    {
      data: taskStages = [],
      isLoading: loadingStages,
      isFetching: fetchingStages,
    },
  ] = useLazyListListQuery();

  const [updateTask, { isLoading: updatingTask }] = useUpdateTaskMutation();

  // methods
  const handleUpdateStage = async (stage) => {
    try {
      await updateTask({
        _id: taskDetail?._id,
        stage,
      }).unwrap();
      toast.info("Lead's stage updated!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update lead's stage!");
    }
  };

  React.useEffect(() => {
    if (!taskDetail?.entity) return;

    fetchStages({
      type: ENTITY_LIST_CONSTANTS.TASK_STAGES,
      isSubType: false,
      includeChildren: true,
      status: "Active",
      entity: taskDetail?.entity?._id,
    });
  }, [fetchStages, taskDetail?.entity]);

  useAppMeta(!taskDetail ? "Task Detail" : `Task - ${taskDetail?.title}`);

  return (
    <TaskDetailContext.Provider value={{ taskDetail, loadingTask: isLoading }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={isLoading}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        {!!taskDetail?.stage && (
          <StagePathway
            isBusy={loadingStages || fetchingStages || updatingTask}
            stages={taskStages}
            getStageLabel={(stage) => stage?.name}
            getStageValue={(stage) => stage?._id}
            activeStage={taskDetail?.stage?.name}
            onChange={handleUpdateStage}
          />
        )}

        <BreadcrumbLayout.Paper>
          <TaskBasicDetail />

          <Divider />

          {!isLoading ? <TaskResponses /> : <RowsSkeleton />}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </TaskDetailContext.Provider>
  );
};

export default TaskDetailScreen;

const PageActions = () => {
  const { taskDetail } = useTaskDetailContext();

  return (
    <div className="flex gap-4">
      <ToggleTaskStatus />

      <Link to={`/app/crm/task/${taskDetail?._id}/edit`}>
        <Button variant="contained" startIcon={<EditOutlined />}>
          Edit
        </Button>
      </Link>
    </div>
  );
};

const ToggleTaskStatus = () => {
  const { taskDetail } = useTaskDetailContext();

  // RTK Query
  const [updateLead, { isLoading: updatingTask }] = useUpdateTaskMutation();

  // methods
  const handleUpdateTaskStatus = async () => {
    try {
      await updateLead({
        _id: taskDetail?._id,
        isActive: !taskDetail?.isActive,
      }).unwrap();
      toast.info(
        `Task successfully ${taskDetail?.isActive ? "Disabled" : "Enabled"}!`
      );
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update Task Status!");
    }
  };

  return (
    <EnableDisableButton
      suffix="Task"
      isActive={taskDetail?.isActive}
      loading={updatingTask}
      onClick={handleUpdateTaskStatus}
    />
  );
};
